import { SET_LOAN_DETAIL } from 'actions/types'
import cloneDeep from 'clone-deep'

import { createReducer } from '../utils'
import { partiesMap } from './partyNames'

export {
  accountTypeTitleForName,
  findCloseParty,
  isRolodexPartyViewKey,
  partiesMap,
  partiesMapWithAccountTypes,
  partyDisplayName,
  partyRecordName,
  partySlotForAccountType,
  partyViewIncludes,
  partyViewKeyTitle,
  partyViewSelectionForOptions,
} from './partyNames'

export const closingPartiesMap: Record<string, string> = {
  titleCompany: 'Title Company',
  hazardInsurance: 'Hazard Insurance',
  escrowCompany: 'Escrow Company',
}

export const partiesMapWitBorrower: Record<string, string> = { ...partiesMap, borrower: 'Borrower' }

export const pullThroughRatioPartiesMap: Record<string, string> = {
  ...partiesMap,
  lender: 'Lender',
  borrower: 'Borrower',
}

export const partiesChangePermissions: Record<string, string> = {
  broker: 'Loan.Detail.Change.Broker',
  underwriter: 'Loan.Detail.Change.Underwriter',
  accountManager: 'Loan.Detail.Change.AccountManager',
  accountExecutive: 'Loan.Detail.Change.AccountExecutive',
  loanProcessor: 'Loan.Detail.Change.LoanProcessor',
  loanOfficer: 'Loan.Detail.Change.LoanOfficer',
}

export enum LoanType {
  DEFAULT = 0,
  CORRESPONDENT = 1,
  RETAIL = 2,
  TABLEFUNDER = 3,
  BROKERED = 4,
}

export const originalLoanTypeMap: Record<string, string> = {
  '0': 'Wholesale',
  '1': 'Correspondent',
  '2': 'Retail',
  '3': 'Table Funder',
  '4': 'Brokered',
}

/** Numeric key → display label. Mutated from Global Settings → Channel Options. */
export let loanTypeMap: Record<string, string> = { ...originalLoanTypeMap }

let visibleLoanTypeKeys: Record<string, boolean> = Object.fromEntries(
  Object.keys(originalLoanTypeMap).map((key) => [key, true]),
)

export type ChannelOptionConfig = { title: string; visible: boolean }

export const defaultChannelOptions = (): Record<string, ChannelOptionConfig> =>
  Object.fromEntries(
    Object.entries(originalLoanTypeMap).map(([key, title]) => [key, { title, visible: true }]),
  )

/**
 * Apply Global Settings channelOptions:
 * - updates loanTypeMap labels for all channels (display)
 * - tracks which channels are visible for pickers
 * - returns filter options keyed by original labels (backend LoanTypeMap)
 */
export const applyChannelOptions = (
  channelOptions?: Record<string, ChannelOptionConfig> | null,
): Record<string, string> => {
  const config = { ...defaultChannelOptions(), ...(channelOptions || {}) }
  const allLabels: Record<string, string> = {}
  const visibleKeys: Record<string, boolean> = {}
  const filterOptions: Record<string, string> = {}

  Object.keys(originalLoanTypeMap).forEach((key) => {
    const item = config[key] || { title: originalLoanTypeMap[key], visible: true }
    const title = item.title || originalLoanTypeMap[key]
    const visible = item.visible !== false
    allLabels[key] = title
    visibleKeys[key] = visible
    if (visible) filterOptions[originalLoanTypeMap[key]] = title
  })

  loanTypeMap = allLabels
  visibleLoanTypeKeys = visibleKeys

  return Object.keys(filterOptions).length
    ? filterOptions
    : Object.fromEntries(Object.entries(originalLoanTypeMap).map(([, title]) => [title, title]))
}

/** Visible channels for pickers, always including the current value if it was hidden. */
export const getLoanTypeMapForSelect = (currentLoanType?: number | string | null): Record<string, string> => {
  const options: Record<string, string> = {}
  Object.keys(visibleLoanTypeKeys).forEach((key) => {
    if (visibleLoanTypeKeys[key]) options[key] = loanTypeMap[key] || originalLoanTypeMap[key]
  })
  if (!Object.keys(options).length) Object.assign(options, loanTypeMap)

  if (currentLoanType === null || currentLoanType === undefined || currentLoanType === '') return options
  const key = String(currentLoanType)
  if (options[key] === undefined && (loanTypeMap[key] || originalLoanTypeMap[key])) {
    options[key] = loanTypeMap[key] || originalLoanTypeMap[key]
  }
  return options
}

/** First In Use channel for admin channel tabs when Wholesale is off. */
export const getFirstVisibleLoanType = (): LoanType => {
  const keys = Object.keys(getLoanTypeMapForSelect())
  if (!keys.length) return LoanType.DEFAULT
  return Number(keys[0]) as LoanType
}

export const getloanDetailInitialState = () => {
  return {
    loanNumber: '',
    byteproFileName: 0,
    entityTitle: '',
    borrowingEntityName: '',
    borrowerName: '',
    propertyAddress: '',
    loanProcessDate: '',
    loanStatus: 'prequal',
    loanSubStatus: '',
    servicingStatus: null,
    servicingPipeline: false,
    includeCoborrower: false,
    loanType: null,
    parties: {},
    closeParties: {},
    rateData: {},
    priceAdjustments: [],
    restructureApplicationRelease: true,
    closingDate: '',
    applicationDate: '',
    emailContacts: [],
    rush: false,
    submitToAE: 0,
    submitToLoanSetup: 0,
    submitConditions: 0,
    submitToUnderwriter: 0,
    termSheetSignedDate: '',
    termSheetSignedPdfKey: '',
    termSheetWetSigned: false,
    termSheetOverride: false,
    pushedToYieldeasyAt: null,
    // crossCollateralBlanketLimit: {},
    // allocatedLoanAmount: [],
    loanGlobalSettings: {},
    pricingEngine: true,
    isCreatedByBorrower: false,
    servicerLoanNumber: null,
    templateNumber: 0,
    loanIntakeTaskCleared: true,
    scopeOfWorkDone: false,
    closingCostEstimate: 0,
    thirdPartyId: '',
    thirdPartyData: {},
    mers: null,
    createdAt: '',
    reviewForm: {},
    // Read-only: the escrow repair holdback the loan was committed at, resolved server-side from
    // the trust sub-ledger when the loan record does not carry it (every converted loan).
    escrowRepairHoldbackFunded: 0,
    /** Report placeholder values for Loan Details floater (loaded once per loan visit). */
    loanDetailsReportValues: {},
  }
}

export const loanDetail = createReducer(getloanDetailInitialState(), {
  [SET_LOAN_DETAIL]: (state: any, payload: any) => {
    let newState = cloneDeep(state)
    newState = {
      ...newState,
      ...payload.data,
    }
    return newState
  },
})
